import React, { useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { navigate } from 'vike/client/router';
import { Binoculars, CircleNotch } from '@phosphor-icons/react';
import { useFollowedRoles } from '../../hooks/useFollowedRoles';
import { useRoleFollowActions } from '../../hooks/useRoleFollowActions';
import { useToast } from '../../contexts/ToastContext';
import { useAuthOptional } from '../../contexts/AuthContext';
import { useAlertUpsellToast } from '../../hooks/useAlertUpsellToast';
import { normalizeSupportedLocale } from '../../utils/routing/locale-from-pathname';
import { localizeRoute } from '../../utils/constants/url-constants';
import { trackEvent } from '../../utils/analytics/analytics';
import { useFormatMessage } from '../../i18n-review/client/Msg';

const messages = defineMessages({
  watch: {
    id: 'role.follow.cta',
    defaultMessage: 'Watch role',
    description: 'Button label to start watching a role (get updates on new postings with this title)',
  },
  watching: {
    id: 'role.follow.watching',
    defaultMessage: 'Watching',
    description: 'Button label when a role is already being watched',
  },
  watchedToast: {
    id: 'role.follow.watchedToast',
    defaultMessage: 'Watching {role}. New postings will show on your watchlist',
    description: 'Toast after starting to watch a role',
  },
  unwatchedToast: {
    id: 'role.follow.unwatchedToast',
    defaultMessage: 'Stopped watching. You won’t be notified of new postings for this role',
    description: 'Toast after unwatching a role',
  },
  undo: {
    id: 'role.follow.undo',
    defaultMessage: 'Undo',
    description: 'Toast action that re-watches the role after accidentally unwatching it',
  },
  viewWatchlist: {
    id: 'company.follow.viewWatchlist',
    defaultMessage: 'View →',
    description: 'Toast action link into the Watchlist',
  },
  createAccount: {
    id: 'role.follow.createAccount',
    defaultMessage: 'Create account',
    description: 'Toast action offering guests an account so a role they just watched isn’t lost',
  },
  emailPromptOffer: {
    id: 'role.follow.emailPrompt.offer',
    defaultMessage: 'Watching {role}. Want an email when new postings appear?',
    description: 'Toast offering to turn on email updates right after starting to watch a role',
  },
  emailPromptTurnOn: {
    id: 'role.follow.emailPrompt.turnOn',
    defaultMessage: 'Turn on',
    description: 'Toast action that enables email updates for watched roles',
  },
  emailPromptEnabled: {
    id: 'role.follow.emailPrompt.enabled',
    defaultMessage: "You're set. New postings in roles you watch will be in your job-alert emails.",
    description: 'Toast confirming email updates were enabled from the role-watch prompt',
  },
  emailPromptPendingConfirmation: {
    id: 'role.follow.emailPrompt.pendingConfirmation',
    defaultMessage: 'Almost there: confirm your email in your inbox to activate updates.',
    description: 'Toast when enabling email updates still needs the account email confirmed',
  },
  emailPromptFailed: {
    id: 'role.follow.emailPrompt.failed',
    defaultMessage: "Couldn't turn on email updates. You can enable them any time from the Alerts page.",
    description: 'Toast when enabling email updates from the role-watch prompt fails',
  },
});

export interface RoleFollowButtonProps {
  /** Localized normalisedTitle exactly as the jobs index facets it. */
  roleTitle: string;
  /** Where this button is placed — recorded on the analytics event. */
  surface?: string;
}

/**
 * A guest's follow/unfollow write is local-only (IndexedDB), so `mutateAsync` above can resolve
 * in well under a frame — the only pending-state cue (`disabled:opacity-60`) never has time to
 * actually paint, which is why the click can look like it did nothing even though it worked (see
 * the investigation this button's spinner/toast changes came out of: watching a role via this
 * exact click DID persist to the watchlist in a live test, but felt like a no-op). Padding `busy`
 * out to a minimum visible duration — regardless of how fast the mutation itself finishes —
 * guarantees the spinner below actually gets a chance to render.
 */
const MIN_VISIBLE_BUSY_MS = 350;

/**
 * Watch/unwatch entry point for role watchlists (SEEKER_HUB_PLAN.md decision 4).
 * The role is stored with the CURRENT UI locale — normalisedTitle facet values
 * are per-locale, so the title only matches the index it was seen in.
 */
export const RoleFollowButton: React.FC<RoleFollowButtonProps> = ({ roleTitle, surface }) => {
  const intl = useIntl();
  const formatMessage = useFormatMessage();
  const locale = normalizeSupportedLocale(intl.locale);
  const { showToast } = useToast();
  const auth = useAuthOptional();
  const followedRoles = useFollowedRoles();
  const { followRole, unfollowRole } = useRoleFollowActions();
  const { maybeOffer } = useAlertUpsellToast({ entityType: 'role', entityId: roleTitle, surface });
  const [busy, setBusy] = useState(false);

  const isWatching = useMemo(() => followedRoles.some((r) => r.roleTitle === roleTitle), [followedRoles, roleTitle]);

  const handleClick = async () => {
    setBusy(true);
    const startedAt = Date.now();
    try {
      if (isWatching) {
        trackEvent({ name: 'role_unfollow', params: { role_title: roleTitle, surface: surface ?? 'unknown', locale: intl.locale } });
        const ok = await unfollowRole(roleTitle);
        if (ok) {
          showToast(formatMessage(messages.unwatchedToast), 'info', undefined, {
            label: formatMessage(messages.undo),
            onClick: () => void followRole(roleTitle, locale),
          });
        }
      } else {
        trackEvent({ name: 'role_follow', params: { role_title: roleTitle, surface: surface ?? 'unknown', locale: intl.locale } });
        const ok = await followRole(roleTitle, locale);
        if (ok) {
          // The same activation wedge as CompanyFollowButton (TEC-514 #3): a watched role is
          // only a live subscription if its updates can actually reach the user.
          const offered = await maybeOffer({
            offer: formatMessage(messages.emailPromptOffer, { role: roleTitle }),
            turnOn: formatMessage(messages.emailPromptTurnOn),
            enabled: formatMessage(messages.emailPromptEnabled),
            pendingConfirmation: formatMessage(messages.emailPromptPendingConfirmation),
            failed: formatMessage(messages.emailPromptFailed),
          });
          if (!offered) {
            // Guests watch locally (IndexedDB) with nothing syncing it anywhere else: pushing
            // "View watchlist" at someone who could lose this the moment they clear site data
            // is less useful than the actual fix: an account. Signed-in users already have that,
            // so they get the watchlist link instead.
            showToast(
              formatMessage(messages.watchedToast, { role: roleTitle }),
              'success',
              3000,
              auth?.isAuthenticated
                ? {
                    label: formatMessage(messages.viewWatchlist),
                    onClick: () => navigate(`/${locale}${localizeRoute('/tools/watchlist', locale)}`),
                  }
                : {
                    label: formatMessage(messages.createAccount),
                    onClick: () => auth?.signIn(undefined, { screenHint: 'signup' }),
                  }
            );
          }
        }
      }
    } finally {
      const elapsed = Date.now() - startedAt;
      if (elapsed < MIN_VISIBLE_BUSY_MS) {
        await new Promise((resolve) => setTimeout(resolve, MIN_VISIBLE_BUSY_MS - elapsed));
      }
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={busy}
      aria-pressed={isWatching}
      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors disabled:opacity-60 ${
        isWatching
          ? 'border-jc-accent bg-jc-accent-muted text-jc-accent hover:bg-jc-accent-muted/70'
          : 'border-jc-border bg-jc-surface text-jc-ink-secondary hover:bg-jc-elevated hover:text-jc-ink'
      }`}
    >
      {busy ? (
        <CircleNotch className="h-3.5 w-3.5 animate-spin" aria-hidden />
      ) : (
        <Binoculars className="h-3.5 w-3.5" weight={isWatching ? 'fill' : 'bold'} aria-hidden />
      )}
      {formatMessage(isWatching ? messages.watching : messages.watch)}
    </button>
  );
};
